'use client';

import React, { useState } from 'react';
import Loading from '../core/Loading';
import { MaterialReactTable } from 'material-react-table';
import { Box, Button, Typography, Modal, IconButton } from '@mui/material';
import { RESOURCE_TYPE, UPLOAD_COLLECTION_METHOD } from '@/stack-shared/constants/resource-constants';
import { useResourcesByModel, useUploadResourceFile } from '@/hooks/api/useResources';
import { useNotification } from '@/contexts/NotificationContext';
import GenericFormModal from './GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from './GenericForm';
import { Add, AddBoxOutlined, AddCircleOutlineOutlined, PlusOneRounded, Close } from '@mui/icons-material';

interface IGenericResourceTable {
    model: string;
    modelId: number;
}

const GenericResourceTable: React.FC<IGenericResourceTable> = (props: IGenericResourceTable) => {
    const { data: resourcesData } = useResourcesByModel(props.model, props.modelId);
    const uploadResourceFileMutation = useUploadResourceFile();
    const { showSuccess, showError } = useNotification();

    const [createResourceModalVisible, setCreateResourceModalVisible] = React.useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');

    const resources = resourcesData ? (Array.isArray(resourcesData) ? resourcesData : Object.values(resourcesData)) : [];

    const createResourceFields = [{
        groupName: 'Location Details',
        fields: [
            { label: 'Name', name: 'name', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
            { label: 'Description', name: 'description', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
            {
                label: "Upload File",
                name: "uploadedFile",
                type: GENERIC_FORM_FIELD_TYPE.FILE,
                options: [{ label: "Image", value: RESOURCE_TYPE.IMAGE }] // or [{ label: "File", value: RESOURCE_TYPE.FILE }]
            }
        ],
    }];

    const handleImageClick = (imageUrl: string) => {
        setSelectedImageUrl(imageUrl);
        setImageModalOpen(true);
    };

    const handleCloseImageModal = () => {
        setImageModalOpen(false);
    };

    // Table Column Definitions
    const resourceColumns = [
        {
            accessorKey: 'signedUrl', header: 'Item',
            Cell: ({ row }: any) => {
                if (row?.original?.type === RESOURCE_TYPE.IMAGE) {
                    return (
                        <img 
                            src={row?.original?.signedUrl} 
                            style={{ 
                                width: 60, 
                                height: 60, 
                                objectFit: 'cover',
                                cursor: 'pointer' 
                            }} 
                            onClick={() => handleImageClick(row?.original?.signedUrl)}
                        />
                    );
                }
                else {
                    return <img src={"/location_default.png"} style={{ width: 60, height: 60, objectFit: 'cover' }} />
                }
            }
        },
        { accessorKey: 'name', header: 'Name' },
        { accessorKey: 'description', header: 'Description' },
        { accessorKey: 'fileName', header: 'fileName' },
    ];

    return (
        <>
            <GenericFormModal
                visible={createResourceModalVisible}
                onDismiss={() => setCreateResourceModalVisible(false)}
                onSubmit={(values) => {

                    if (!values.uploadedFile) {
                        console.warn("No file selected for upload.");
                        return;
                    }

                    const selectedFile = values.uploadedFile; // Extract the file object

                    const fileExtension = selectedFile.name.split('.').pop(); // Get file extension
                    if (!fileExtension) {
                        console.warn("Invalid file format.");
                        return;
                    }

                    const uploadData = {
                        name: values.name || selectedFile.name,
                        description: values.description || '',
                        model: props.model,
                        modelId: props.modelId,
                        extension: fileExtension,
                        fileName: `${props.modelId}-resource.${fileExtension}`,
                        originalFileName: selectedFile.name,
                        uploadCollectionMethod: UPLOAD_COLLECTION_METHOD.INCREMENT,
                        type: RESOURCE_TYPE.IMAGE, // Assuming this is an image upload
                        file: selectedFile,
                        rootPath: props.model,
                        primary: true,
                        conversions: [],
                        fileSize: selectedFile.size,
                        parts: 1
                    };

                    //console.log("Uploading File:", uploadData);

                    uploadResourceFileMutation.mutate(uploadData, {
                        onSuccess: () => {
                            console.log("Upload successful");
                            setCreateResourceModalVisible(false);
                            showSuccess('File uploaded successfully');
                        },
                        onError: (error: any) => {
                            console.error("File upload failed.");
                            showError(error.message || 'File upload failed.');
                        },
                    });
                }}
                initialValues={{

                }}
                fieldGroups={createResourceFields}
                title={''} />

            {/* Image Modal */}
            <Modal
                open={imageModalOpen}
                onClose={handleCloseImageModal}
                aria-labelledby="image-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                }}
            >
                <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                    <IconButton
                        onClick={handleCloseImageModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'white',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <Close />
                    </IconButton>
                    <img 
                        src={selectedImageUrl} 
                        alt="Full size resource" 
                        style={{ 
                            maxWidth: '100%', 
                            maxHeight: '90vh', 
                            objectFit: 'contain' 
                        }} 
                    />
                </Box>
            </Modal>

            <Box sx={{}}>
                <MaterialReactTable
                    columns={resourceColumns}
                    data={resources}
                    initialState={{ density: 'compact' }}
                    renderTopToolbarCustomActions={() => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddCircleOutlineOutlined
                          style={{ cursor: 'pointer' }}
                          onClick={() => setCreateResourceModalVisible(true)}
                        />
                        <Typography variant="h6" sx={{ m: 0 }}>
                          Resources
                        </Typography>
                      </Box>
                    )}
                />
            </Box>
        </>
    );
};

export default GenericResourceTable;
